import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, GUEST_ENDPOINTS, MASTER_DATA_BASE, USER_ENDPOINTS } from "./apifactory";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Accept-Language": "en",
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("accessToken") || localStorage.getItem("authAccessToken");
  if (token) {
    config.headers.Authorization = `${token}`;
  }
  return config;
});

export const loginAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.login, payload);
  return response.data;
};

export const logoutAction = async (deviceId) => {
  const response = await apiClient.post(API_ENDPOINTS.logout, {
    device_id: deviceId,
  });
  return response.data;
};

export const registerDeviceAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.registerDevice, payload);
  return response.data;
};

export function clearAdminAuthStorage() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("authAccessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("authResponse");
  localStorage.removeItem("authUser");
  localStorage.removeItem("isLoggedIn");
  localStorage.removeItem("fcmToken");
  // device_id is intentionally kept — persists across logins per API spec
}

const DEVICE_ID_KEY = "device_id";
const LEGACY_DEVICE_ID_KEY = "adminDeviceId";

/** @deprecated use getOrCreateDeviceId from utils/deviceInfo — kept for logout/topbar */
export function getOrCreateAdminDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = localStorage.getItem(LEGACY_DEVICE_ID_KEY);
    if (id) {
      localStorage.setItem(DEVICE_ID_KEY, id);
      localStorage.removeItem(LEGACY_DEVICE_ID_KEY);
    }
  }
  if (!id) {
    id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export const forgotPasswordAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.forgotPassword, payload);
  return response.data;
};

export const verifyOtpAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.verifyOtp, payload);
  return response.data;
};

export const resetPasswordAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.resetPassword, payload);
  return response.data;
};

export const fetchUsersAction = async ({ page = 1, perPage = 10, role = 0 } = {}) => {
  const response = await apiClient.get(API_ENDPOINTS.usersFetch, {
    params: { page, perPage, role },
  });
  return response.data;
};

export const fetchUserByIdAction = async (userId) => {
  const response = await apiClient.get(`${API_ENDPOINTS.usersFetch}/${userId}`);
  return response.data;
};

export const updateBrokerRequestAction = async (payload) => {
  const response = await apiClient.post(API_ENDPOINTS.brokerRequestUpdate, {
    id: payload.id,
    status: payload.status,
    rejectionReason: payload.rejectionReason ?? "",
  });
  return response.data;
};

export const fetchAnnouncementsAction = async ({ page = 1, perPage = 10, status = 2, user_role } = {}) => {
  const params = { page, perPage, status };
  if (user_role != null && user_role !== "") {
    params.user_role = user_role;
  }
  const response = await apiClient.get(API_ENDPOINTS.announcementsFetch, {
    params,
  });
  return response.data;
};

export const fetchAnnouncementByIdAction = async (id) => {
  const response = await apiClient.get(`${API_ENDPOINTS.announcementsFetch}/${id}`);
  return response.data;
};

export const deleteAnnouncementAction = async (id) => {
  const response = await apiClient.delete(`${API_ENDPOINTS.announcementsDelete}/${id}`);
  return response.data;
};

/** Approve: { announcement_id, status: 2 }. Reject: { announcement_id, status: 3, rejection_reason }. */
export const updateAnnouncementStatusAction = async ({ announcement_id, status, rejection_reason = "" }) => {
  const body = { announcement_id, status };
  if (Number(status) === 3) {
    body.rejection_reason = rejection_reason;
  }
  const response = await apiClient.put(API_ENDPOINTS.announcementsUpdateStatus, body);
  return response.data;
};

function masterDataBase(resource) {
  const base = MASTER_DATA_BASE[resource];
  if (!base) throw new Error(`Unknown master data resource: ${resource}`);
  return base;
}

export const fetchMasterDataAction = async (resource, { page = 1, perPage = 10, ...extraParams } = {}) => {
  const response = await apiClient.get(`${masterDataBase(resource)}/fetch`, {
    params: { page, perPage, ...extraParams },
  });
  return response.data;
};

export const fetchMasterDataByIdAction = async (resource, id) => {
  const response = await apiClient.get(`${masterDataBase(resource)}/fetch/${id}`);
  return response.data;
};

export const addMasterDataAction = async (resource, payload) => {
  const response = await apiClient.post(`${masterDataBase(resource)}/add`, payload);
  return response.data;
};

export const editMasterDataAction = async (resource, payload) => {
  const response = await apiClient.put(`${masterDataBase(resource)}/edit`, payload);
  return response.data;
};

export const deleteMasterDataAction = async (resource, id) => {
  const response = await apiClient.delete(`${masterDataBase(resource)}/delete/${id}`);
  return response.data;
};

export const fetchUserPropertyTypesAction = async ({ page = 1, perPage = 100 } = {}) => {
  const response = await apiClient.get(USER_ENDPOINTS.fetchPropertyTypes, {
    params: { page, perPage },
  });
  return response.data;
};

export const addToWishlistAction = async (announcementId) => {
  const response = await apiClient.post(USER_ENDPOINTS.wishlistAdd, {
    announcement_id: announcementId,
  });
  return response.data;
};

export const fetchWishlistedAnnouncementsAction = async ({ page = 1, perPage = 10 } = {}) => {
  const response = await apiClient.get(USER_ENDPOINTS.wishlistFetch, {
    params: { page, perPage },
  });
  return response.data;
};

export const removeFromWishlistAction = async (announcementId) => {
  const response = await apiClient.delete(`${USER_ENDPOINTS.wishlistRemove}/${announcementId}`);
  return response.data;
};

export const fetchGuestAnnouncementsAction = async ({ page = 1, perPage = 10, user_role } = {}) => {
  const params = { page, perPage };
  if (user_role != null && user_role !== "") {
    params.user_role = user_role;
  }
  const response = await apiClient.get(GUEST_ENDPOINTS.announcementsFetchAll, { params });
  return response.data;
};

export const fetchGuestAnnouncementByIdAction = async (id) => {
  const response = await apiClient.get(`${GUEST_ENDPOINTS.announcementsFetch}/${id}`);
  return response.data;
};
